<template>
  <page-loader>
    <v-container>
      <v-row>
        <v-col class="d-flex">
          <v-breadcrumbs :items="breadcrumbs" class="px-0 py-0" />
        </v-col>
        <v-col cols="auto" class="text-right">
          <v-btn
            :to="{
              name: department === 'organic' ? 'offers.promotions.performance' : 'offers.promotions.organic',
              query: { offerType: query.params.offerType },
            }"
            color="primary"
            depressed
          >
            <v-icon left>switch_access_shortcut</v-icon>
            <span>Switch to {{ department === 'organic' ? 'Performance' : 'Organic' }}</span>
          </v-btn>
          <v-btn
            :to="{
              name: 'offers.table-preview',
              query: { department, site: query.params.site, offerType: query.params.offerType },
            }"
            class="ml-4"
            color="blue"
            dark
            depressed
            >Table Preview</v-btn
          >
        </v-col>
      </v-row>
      <v-row class="mt-0">
        <v-col>
          <v-divider />
        </v-col>
      </v-row>
    </v-container>
    <v-container>
      <v-row no-gutters>
        <v-col class="d-flex align-center justify-start">
          <select-list
            v-model="query.params.site"
            :disabled="isPerformingAction"
            :items="siteOptions"
            custom-type="avatar"
            label="Website"
            class="mr-4"
            hide-details
          />
          <select-list
            v-model="query.params.offerType"
            :disabled="isPerformingAction"
            :items="getItemsForOption('offerTypes')"
            label="Offer Type"
            class="mr-4"
            hide-details
          />
        </v-col>
        <v-col class="d-flex align-center justify-end">
          <search-input v-model="searchTerm" :disabled="isLoading || hasErrors" :is-loading="isLoading" hide-details />
        </v-col>
      </v-row>
    </v-container>
    <v-container>
      <v-row justify="space-between">
        <v-col sm="5">
          <v-card class="pa-2" outlined>
            <v-list class="py-0" two-line>
              <promotion-dropzone
                v-for="(list, position) in paidPositions"
                :key="position"
                v-model="paidPositions[position]"
                :is-disabled="!isPermittedTo('update')"
                :position="Number(position) + 1"
                group="paid-position"
                @delete="onPositionDelete"
              />
            </v-list>
          </v-card>
        </v-col>
        <v-col sm="7">
          <offers-list
            :draggable-group="{ name: 'paid-position', pull: 'clone', put: false }"
            :is-disabled="!isPermittedTo('update')"
            :items="filteredOffers"
            :pagination="pagination"
            draggable
            @page-change="onPageChange"
          />
        </v-col>
      </v-row>
    </v-container>
    <ordering-modal
      v-model="showSaveModal"
      :filters="query.params"
      :is-loading="isLoading"
      :previous="paidOffersArrayOriginal"
      :current="paidOffersArrayUpdated"
      @modal-confirm="onSaveConfirm"
    />
    <fab-actions
      :has-changes="hasChanges"
      context="Order"
      @reset="showResetModal = true"
      @save="showSaveModal = true"
    />
    <reset-modal v-model="showResetModal" category="ordering" @modal-confirm="onResetConfirm" />
    <exit-modal :value="showExitModal" @modal-confirm="confirmExitBeforeSave" @modal-cancel="cancelExitBeforeSave" />
  </page-loader>
</template>

<script>
import { cloneDeep, merge, capitalize } from 'lodash-es'
import { mapGetters, mapMutations } from 'vuex'

import FabActions from '@/components/fab-actions'
import OrderingModal from '@/components/modals/ordering-modal'
import PageLoader from '@/components/page-loader'
import PromotionDropzone from '@/views/offers/components/promotion-dropzone'
import ResetModal from '@/components/modals/reset-modal'
import SearchInput from '@/components/forms/search-input'
import SelectList from '@/components/forms/select-list'

import DataModification from '@/mixins/data-modification'
import PermissionsHandler from '@/mixins/permissions-handler'
import QueryParamsHandler from '@/mixins/query-params-handler'

import { offers, sites } from '@/services/offers'

import OffersList from '@/views/offers/components/offers-list'

const positionsModel = positions =>
  Array(positions)
    .fill(null)
    .reduce((acc, _, index) => {
      acc[index] = []
      return acc
    }, {})

export default {
  name: 'PromotionsManagement',
  components: {
    FabActions,
    OffersList,
    OrderingModal,
    PageLoader,
    PromotionDropzone,
    ResetModal,
    SearchInput,
    SelectList,
  },
  mixins: [DataModification, PermissionsHandler, QueryParamsHandler],
  props: {
    department: {
      type: String,
      required: true,
      validator(value) {
        return ['organic', 'performance'].includes(value)
      },
    },
  },
  data: () => ({
    isLoading: false,
    defaultQuery: {
      site: null,
    },
    queriesDefined: ['offerType', 'site'],
    queryDefaults: ['offerType', 'site'],
    queryPagination: false,
    querySearch: false,
    possiblePositions: 10,
    offers: [],
    promotions: [],
    paidPositions: [],
    page: 1,
    searchTerm: '',
    siteOptions: [],
    showResetModal: false,
    showSaveModal: false,
  }),
  computed: {
    ...mapGetters('general', ['hasPageErrors', 'isPageLoading']),
    ...mapGetters('options', ['getItemsForOption', 'hasOptions']),
    breadcrumbs() {
      return [
        { text: 'Offers', to: { name: 'offers.management' }, exact: true },
        { text: 'Promotions', to: { name: 'offers.promotions' }, exact: true },
        { text: capitalize(this.department || 'loading'), disabled: true },
      ]
    },
    filteredOffers() {
      return this.offers
        .filter(({ published }) => published)
        .filter(({ departments = [] }) => departments.includes(this.department))
        .filter(({ slug }) => !Object.keys(this.paidOffersUpdated).includes(slug))
        .filter(({ name }) => !this.searchTerm || name.toLowerCase().includes(this.searchTerm.toLowerCase()))
    },
    hasErrors() {
      return !this.hasOptions || this.hasPageErrors
    },
    selectedSiteData() {
      return this.promotions.find(({ hostname }) => hostname === this.query.params.site)
    },
    paidOffers() {
      return Object.entries(this.paidOffersOriginal).map(([slug, position]) => ({
        position,
        offer: this.findOfferData(slug),
      }))
    },
    paidOffersOriginal() {
      return (this.selectedSiteData && this.selectedSiteData.promotions[this.query.params.offerType]) || {}
    },
    paidOffersUpdated() {
      return Object.values(this.paidPositions).reduce((acc, position, index) => {
        if (!position.length) return acc
        const [partner] = position
        acc[partner.slug] = index
        return acc
      }, {})
    },
    hasChanges() {
      return this.hasDataChanged(this.paidOffersOriginal, this.paidOffersUpdated)
    },
    paidOffersArrayOriginal() {
      return this.buildPositionChangeArray(Object.entries(this.paidOffersOriginal))
    },
    paidOffersArrayUpdated() {
      return this.buildPositionChangeArray(Object.entries(this.paidOffersUpdated))
    },
    isPerformingAction() {
      return this.isLoading || this.hasErrors || this.hasChanges
    },
  },
  watch: {
    paidOffers: {
      handler() {
        this.updatePaidPositions()
      },
      deep: true,
    },
  },
  async created() {
    this.setBeforeUnloadListener(this.onBeforeUnload)
    await this.getSites()
    this.siteOptions = this.promotions
      .map(({ name, hostname, assets: { favicon } = {} }) => ({ key: name, value: hostname, icon: favicon }))
      .sort((a, b) => a.key.localeCompare(b.key))

    this.setDefaultQueryParams()
    this.setPageLoading(false)
  },
  beforeDestroy() {
    this.removeBeforeUnloadListener(this.onBeforeUnload)
  },
  beforeRouteLeave(to, from, next) {
    if (!this.hasChanges) return next()
    this.handleExitBeforeSave(next)
  },
  methods: {
    ...mapMutations('general', ['setPageLoading', 'setPageErrors']),
    onBeforeUnload(event) {
      if (!this.hasChanges) return
      this.confirmUnload(event)
    },
    updatePaidPositions() {
      this.paidPositions = this.paidOffers.reduce((acc, { offer, position }) => {
        acc[position] = [offer]
        return acc
      }, positionsModel(this.possiblePositions))
    },
    async getSites() {
      try {
        const { data } = await sites.getAllSites({ filters: { department: this.department } })
        this.promotions = data
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to fetch sites',
        })
      }
    },
    findOfferData(slug) {
      return this.offers.find(offer => offer.slug === slug) || {}
    },
    onPositionDelete(position) {
      this.$set(this.paidPositions, position - 1, [])
    },
    async onSaveConfirm() {
      this.isLoading = true
      try {
        const { id } = this.selectedSiteData

        const cloned = cloneDeep(this.selectedSiteData)
        delete cloned.promotions[this.query.params.offerType]

        const updated = merge(cloned, {
          promotions: {
            [this.query.params.offerType]: this.paidOffersUpdated,
          },
        })

        await sites.updateSite(id, updated)
        await this.getSites()
        this.showSaveModal = false
        this.$notify({ type: 'success', text: 'Promotions updated' })
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to update paid promotions. Please try again',
        })
      }

      this.isLoading = false
    },
    onResetConfirm() {
      this.resetOffers()
      this.$notify({ type: 'success', text: 'Promotions reset back to original state' })
    },
    resetOffers() {
      this.updatePaidPositions()
      this.showResetModal = false
    },
    buildPositionChangeArray(positions) {
      return Array(this.possiblePositions)
        .fill(null)
        .map((slot, index) => {
          const partner = positions.find(([, pos]) => index === pos)

          if (!partner)
            return {
              name: 'Empty slot',
              slug: `empty-slot-${this.query.params.offerType}-${index}`,
            }

          const [slug] = partner
          return this.findOfferData(slug)
        })
    },
    async getOffers() {
      this.isLoading = true

      try {
        const { data } = await offers.getAllOffers({
          compact: true,
          properties: ['departments'],
          filters: this.queryFilters,
        })
        this.offers = data
        this.updatePaidPositions()
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve offers',
        })
      }

      this.isLoading = false
    },
    async handleQueryUpdate() {
      await this.getOffers()
    },
    async onPageChange(page) {
      this.pagination.currentPage = page
    },
    setDefaultQueryParams(reset) {
      /* SITE */
      // Set default site to first in array if not set.
      let defaultSite = this.siteOptions[0].value
      this.$set(this.defaultQuery, 'site', defaultSite)

      if (!reset) {
        if (this.query.params.site) {
          const defaultQuerySite = this.siteOptions.find(({ value }) => value === this.query.params.site)
          if (defaultQuerySite) defaultSite = defaultQuerySite.value
        }
      }
      this.$set(this.query.params, 'site', defaultSite)
    },
  },
}
</script>
